// pages/configuracion/AsignacionInternos.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, FormControl,
  Grid, InputLabel, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../../components/common/PageHeader';
import { useNotification } from '../../contexts/NotificationContext';
import usuarioEspecialistaService from '../../services/usuarioEspecialistaService';
import especialistaService from '../../services/especialistaService';

const AsignacionInternos = () => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [internos, setInternos] = useState([]);
  const [especialistas, setEspecialistas] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [selectedInterno, setSelectedInterno] = useState('');
  const [selectedEspecialista, setSelectedEspecialista] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Cargar datos iniciales
        const [internosRes, especialistasRes, asignacionesRes] = await Promise.all([
          usuarioEspecialistaService.getInternosDisponibles(),
          especialistaService.getEspecialistas(),
          usuarioEspecialistaService.getAsignaciones()
        ]);

        setInternos(internosRes.data);
        setEspecialistas(especialistasRes.data);
        setAsignaciones(asignacionesRes.data);
      } catch (error) {
        console.error('Error cargando datos:', error);
        showNotification('Error al cargar los datos', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey, showNotification]);

  const handleAsignar = async () => {
    if (!selectedInterno || !selectedEspecialista) {
      showNotification('Debes seleccionar un interno y un especialista', 'warning');
      return;
    }

    try {
      await usuarioEspecialistaService.asignarInterno({
        usuario_id: selectedInterno,
        especialista_id: selectedEspecialista
      });

      showNotification('Asignación creada exitosamente', 'success');
      setSelectedInterno('');
      setSelectedEspecialista('');
      setRefreshKey(prev => prev + 1); // Refrescar datos
    } catch (error) {
      console.error('Error al asignar:', error);
      const errorMsg = error.response?.data?.message || 'Error al crear la asignación';
      showNotification(errorMsg, 'error');
    }
  };

  const handleEliminar = async (id) => {
    try {
      await usuarioEspecialistaService.eliminarAsignacion(id);
      showNotification('Asignación eliminada exitosamente', 'success');
      setRefreshKey(prev => prev + 1); // Refrescar datos
    } catch (error) {
      console.error('Error al eliminar:', error);
      showNotification('Error al eliminar la asignación', 'error');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Asignación de Internos a Especialistas"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Configuración', to: '/configuracion' },
          { text: 'Asignación de Internos' }
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Nueva Asignación
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel id="interno-label">Interno</InputLabel>
                <Select
                  labelId="interno-label"
                  value={selectedInterno}
                  onChange={(e) => setSelectedInterno(e.target.value)}
                  label="Interno"
                >
                  <MenuItem value="">Seleccione un interno</MenuItem>
                  {internos.map(interno => (
                    <MenuItem key={interno.id} value={interno.id}>
                      {interno.nombre} {interno.apellidos}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel id="especialista-label">Especialista</InputLabel>
                <Select
                  labelId="especialista-label"
                  value={selectedEspecialista}
                  onChange={(e) => setSelectedEspecialista(e.target.value)}
                  label="Especialista"
                >
                  <MenuItem value="">Seleccione un especialista</MenuItem>
                  {especialistas.map(especialista => (
                    <MenuItem key={especialista.id} value={especialista.id}>
                      {especialista.usuario?.nombre} {especialista.usuario?.apellidos}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ height: '56px' }}
                onClick={handleAsignar}
                disabled={!selectedInterno || !selectedEspecialista}
              >
                Asignar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Asignaciones Actuales
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : asignaciones.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No hay asignaciones configuradas
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Interno</TableCell>
                    <TableCell>Especialista</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {asignaciones.map(asignacion => (
                    <TableRow key={asignacion.id}>
                      <TableCell>
                        {asignacion.usuario?.nombre} {asignacion.usuario?.apellidos}
                      </TableCell>
                      <TableCell>
                        {asignacion.especialista?.usuario?.nombre} {asignacion.especialista?.usuario?.apellidos}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleEliminar(asignacion.id)}
                        >
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AsignacionInternos;